/**
 * Chat Dashboard Page
 * Full-featured chat interface for agents and admins to manage conversations.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  MessageCircle, Send, Loader2, User, Clock, CheckCircle, 
  XCircle, UserPlus, Phone, Mail, AlertCircle, Check, CheckCheck,
  Video
} from 'lucide-react';
import { chatService } from '@/services/chatService';
import { useAuth } from '@/contexts/AuthContext';
import { useCall } from '@/components/call';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export default function ChatDashboard() {
  const { user } = useAuth();
  const { startCall, callConfig } = useCall();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all');
  const [typingUser, setTypingUser] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  const activeConversationRef = useRef(null);

  // Keep ref in sync with state
  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  // Connect WebSocket and load conversations
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      chatService.connect(token);
      loadConversations();
    }

    // Set up WebSocket listeners
    const unsubscribe = [
      chatService.on('new_message', handleNewMessage),
      chatService.on('new_conversation', handleNewConversation),
      chatService.on('typing', handleTyping),
      chatService.on('agent_joined', handleAgentJoined),
      chatService.on('conversation_closed', handleConversationClosed),
      chatService.on('message_read', handleMessageRead),
      chatService.on('messages_read', handleMessagesRead),
    ];

    return () => {
      unsubscribe.forEach(unsub => unsub());
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Scroll to bottom when messages change (within scroll container)
  useEffect(() => {
    if (messages.length > 0) {
      // Use parent scroll container instead of scrollIntoView to prevent page scroll
      const scrollArea = messagesEndRef.current?.closest('[data-radix-scroll-area-viewport]');
      if (scrollArea) {
        setTimeout(() => {
          scrollArea.scrollTop = scrollArea.scrollHeight;
        }, 100);
      }
    }
  }, [messages]);

  // Subscribe to active conversation and mark messages as read
  useEffect(() => {
    if (activeConversation) {
      chatService.subscribeToConversation(activeConversation.id);
      // Mark all messages as read when opening conversation
      chatService.markAllRead(activeConversation.id).catch(console.error);
      return () => {
        chatService.unsubscribeFromConversation(activeConversation.id);
      };
    }
  }, [activeConversation?.id]);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const data = await chatService.getConversations();
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const loadConversation = async (conversationId) => {
    setLoadingMessages(true);
    try {
      const data = await chatService.getConversation(conversationId);
      setActiveConversation(data.conversation);
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Failed to load conversation:', error);
      toast.error('Failed to load conversation');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleNewMessage = (data) => {
    const currentConversation = activeConversationRef.current;
    if (data.conversation_id === currentConversation?.id) {
      // Only add if not already present (avoid duplicates from optimistic update)
      setMessages(prev => {
        const exists = prev.some(msg => msg.id === data.message.id || 
          (msg._optimistic && msg.content === data.message.content));
        if (exists) {
          // Replace optimistic message with real one
          return prev.map(msg => 
            msg._optimistic && msg.content === data.message.content
              ? { ...data.message, _optimistic: false }
              : msg
          );
        }
        return [...prev, data.message];
      });
      // Mark the new message as read since conversation is open
      chatService.markMessageRead(data.conversation_id, data.message.id).catch(console.error);
    }
    loadConversations();
  };

  const handleNewConversation = (data) => {
    toast.info('New conversation started', { 
      description: data.conversation?.subject 
    });
    loadConversations();
  };

  const handleTyping = (data) => {
    const currentConversation = activeConversationRef.current;
    if (data.conversation_id === currentConversation?.id && data.user_id !== user?.id) {
      if (data.is_typing) {
        setTypingUser(data.user_name);
      } else {
        setTypingUser(null);
      }
    }
  };

  const handleAgentJoined = (data) => {
    const currentConversation = activeConversationRef.current;
    if (data.conversation_id === currentConversation?.id) {
      setMessages(prev => [...prev, data.message]);
    }
    loadConversations();
  };

  const handleConversationClosed = (data) => {
    const currentConversation = activeConversationRef.current;
    if (data.conversation_id === currentConversation?.id) {
      setActiveConversation(prev => ({ ...prev, status: 'closed' }));
      setMessages(prev => [...prev, data.message]);
    }
    loadConversations();
  };

  const handleMessageRead = (data) => {
    const currentConversation = activeConversationRef.current;
    // Update the read status of a single message
    if (data.conversation_id === currentConversation?.id) {
      setMessages(prev => prev.map(msg => 
        msg.id === data.message_id 
          ? { ...msg, read_at: data.read_at }
          : msg
      ));
    }
  };

  const handleMessagesRead = (data) => {
    const currentConversation = activeConversationRef.current;
    // Update the read status of multiple messages
    if (data.conversation_id === currentConversation?.id) {
      setMessages(prev => prev.map(msg => 
        data.message_ids.includes(msg.id)
          ? { ...msg, read_at: data.read_at }
          : msg
      ));
    }
  };

  const sendTypingStatus = useCallback((isTyping) => {
    if (activeConversation && isTypingRef.current !== isTyping) {
      isTypingRef.current = isTyping;
      const userName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Agent';
      chatService.sendTyping(activeConversation.id, userName, isTyping);
    }
  }, [activeConversation, user]);

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    if (e.target.value.trim()) {
      sendTypingStatus(true);
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingStatus(false);
      }, 2000);
    } else {
      sendTypingStatus(false);
    }
  };

  const handleInputBlur = () => {
    // Stop typing when input loses focus
    sendTypingStatus(false);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;

    // Stop typing indicator
    sendTypingStatus(false);

    const messageContent = newMessage.trim();
    setSending(true);
    
    // Optimistic UI update - add message immediately
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      content: messageContent,
      sender_id: user?.id,
      sender_name: user?.full_name || user?.email,
      message_type: 'text',
      created_at: new Date().toISOString(),
      read_at: null,
      _optimistic: true
    };
    setMessages(prev => [...prev, optimisticMessage]);
    setNewMessage('');
    
    try {
      const response = await chatService.sendMessage(activeConversation.id, messageContent);
      // Replace optimistic message with real one - API returns { success: true, message: {...} }
      const actualMessage = response.message || response;
      setMessages(prev => prev.map(msg => 
        msg._optimistic && msg.id === optimisticMessage.id 
          ? { ...actualMessage, _optimistic: false }
          : msg
      ));
      inputRef.current?.focus();
    } catch (error) {
      console.error('Failed to send message:', error);
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleJoinConversation = async () => {
    if (!activeConversation) return;

    try {
      await chatService.joinConversation(activeConversation.id);
      toast.success('Joined conversation');
      loadConversation(activeConversation.id);
    } catch (error) {
      console.error('Failed to join conversation:', error);
      toast.error('Failed to join conversation');
    }
  };

  const handleCloseConversation = async () => {
    if (!activeConversation) return;

    try {
      await chatService.closeConversation(activeConversation.id);
      toast.success('Conversation closed');
    } catch (error) {
      console.error('Failed to close conversation:', error);
      toast.error('Failed to close conversation');
    }
  };

  const filteredConversations = conversations.filter(conv => {
    if (filter === 'all') return true;
    return conv.status === filter;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-3 w-3 text-green-500" />;
      case 'waiting': return <Clock className="h-3 w-3 text-yellow-500" />;
      case 'closed': return <XCircle className="h-3 w-3 text-gray-500" />;
      default: return <AlertCircle className="h-3 w-3" />;
    }
  };

  // Read receipt indicator component
  const ReadReceipt = ({ message }) => {
    if (message.sender_id !== user?.id) return null;
    
    return (
      <span className="inline-flex ml-1">
        {message.read_at ? (
          <CheckCheck className="h-3 w-3 text-blue-400" title={`Read at ${new Date(message.read_at).toLocaleString()}`} />
        ) : (
          <Check className="h-3 w-3 opacity-60" title="Delivered" />
        )}
      </span>
    );
  };

  return (
    <DashboardLayout role={user?.role || 'admin'}>
      <div className="flex flex-col h-[calc(100vh-120px)]" data-testid="chat-dashboard">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold">Live Chat</h1>
            <p className="text-muted-foreground">Manage customer conversations</p>
          </div>
          <Badge variant="outline" className="text-lg px-3 py-1" data-testid="waiting-badge">
            {conversations.filter(c => c.status === 'waiting').length} waiting
          </Badge>
        </div>

        <div className="flex-1 grid grid-cols-12 gap-4 min-h-0 overflow-hidden">
          {/* Conversation List */}
          <Card className="col-span-4 flex flex-col min-h-0 overflow-hidden">
            <CardHeader className="pb-2">
              <Tabs value={filter} onValueChange={setFilter}>
                <TabsList className="grid grid-cols-3 w-full">
                  <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
                  <TabsTrigger value="waiting" data-testid="filter-waiting">
                    Waiting
                    {conversations.filter(c => c.status === 'waiting').length > 0 && (
                      <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 text-xs">
                        {conversations.filter(c => c.status === 'waiting').length}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="active" data-testid="filter-active">Active</TabsTrigger>
                </TabsList>
              </Tabs>
            </CardHeader>
            <CardContent className="flex-1 p-0 overflow-hidden">
              <ScrollArea className="h-full">
                {loading ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground">
                    No conversations
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredConversations.map(conv => (
                      <button
                        key={conv.id}
                        onClick={() => loadConversation(conv.id)}
                        className={cn(
                          "w-full p-3 text-left hover:bg-muted transition-colors",
                          activeConversation?.id === conv.id && "bg-muted"
                        )}
                        data-testid={`conversation-item-${conv.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm truncate flex-1">
                            {conv.client_name || 'Unknown Client'}
                          </span>
                          <div className="flex items-center gap-1">
                            {getStatusIcon(conv.status)}
                            {conv.unread_count > 0 && (
                              <Badge variant="destructive" className="text-xs h-5 w-5 p-0 flex items-center justify-center">
                                {conv.unread_count}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{conv.subject}</p>
                        <p className="text-xs text-muted-foreground truncate mt-1">
                          {conv.last_message || 'No messages'}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {conv.last_message_at && new Date(conv.last_message_at).toLocaleString()}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Chat Area */}
          <Card className="col-span-8 flex flex-col min-h-0 overflow-hidden">
            {activeConversation ? (
              <>
                {/* Chat Header */}
                <CardHeader className="pb-2 border-b">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg" data-testid="active-conversation-client">
                        {activeConversation.client_name || 'Client'}
                      </CardTitle>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                        {activeConversation.client_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {activeConversation.client_email}
                          </span>
                        )}
                        {activeConversation.client_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {activeConversation.client_phone}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Video Call Button */}
                      {callConfig?.enabled && activeConversation.status === 'active' && activeConversation.client_id && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => startCall({
                            clientId: activeConversation.client_id,
                            conversationId: activeConversation.id,
                            callType: 'video'
                          })}
                          data-testid="video-call-btn"
                        >
                          <Video className="h-4 w-4 mr-1" />
                          Call
                        </Button>
                      )}
                      {activeConversation.status === 'waiting' && (
                        <Button size="sm" onClick={handleJoinConversation} data-testid="join-chat-btn">
                          <UserPlus className="h-4 w-4 mr-1" />
                          Join Chat
                        </Button>
                      )}
                      {activeConversation.status === 'active' && (
                        <Button size="sm" variant="outline" onClick={handleCloseConversation} data-testid="close-chat-btn">
                          Close Chat
                        </Button>
                      )}
                      <Badge variant={
                        activeConversation.status === 'active' ? 'default' :
                        activeConversation.status === 'waiting' ? 'warning' : 'secondary'
                      }>
                        {activeConversation.status}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>

                {/* Messages */}
                <CardContent className="flex-1 p-0 overflow-hidden">
                  <ScrollArea className="h-full p-4">
                    {loadingMessages ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {messages.map(msg => (
                          <div
                            key={msg.id}
                            className={cn(
                              "flex",
                              msg.sender_id === user?.id ? "justify-end" : "justify-start"
                            )}
                            data-testid={`message-${msg.id}`}
                          >
                            {msg.message_type === 'system' ? (
                              <div className="text-xs text-muted-foreground text-center w-full py-1">
                                {msg.content}
                              </div>
                            ) : (
                              <div
                                className={cn(
                                  "max-w-[70%] rounded-lg p-3",
                                  msg.sender_id === user?.id
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted"
                                )}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <User className="h-3 w-3" />
                                  <span className="text-xs font-medium">
                                    {msg.sender_name} ({msg.sender_role})
                                  </span>
                                </div>
                                <p className="whitespace-pre-wrap">{msg.content}</p>
                                <div className="flex items-center justify-end mt-1">
                                  <p className="text-[10px] opacity-60">
                                    {new Date(msg.created_at).toLocaleDateString()} {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                  <ReadReceipt message={msg} />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        {typingUser && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="typing-indicator">
                            <span className="flex gap-1">
                              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </span>
                            {typingUser} is typing...
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>

                {/* Message Input */}
                {activeConversation.status !== 'closed' ? (
                  <div className="p-4 border-t">
                    {activeConversation.status === 'waiting' ? (
                      <div className="text-center text-muted-foreground">
                        Click "Join Chat" to respond to this conversation
                      </div>
                    ) : (
                      <form onSubmit={handleSendMessage} className="flex gap-2">
                        <Input
                          ref={inputRef}
                          value={newMessage}
                          onChange={handleInputChange}
                          onBlur={handleInputBlur}
                          placeholder="Type your message..."
                          disabled={sending}
                          className="flex-1"
                          data-testid="agent-message-input"
                        />
                        <Button type="submit" disabled={sending || !newMessage.trim()} data-testid="agent-send-btn">
                          {sending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </Button>
                      </form>
                    )}
                  </div>
                ) : (
                  <div className="p-4 border-t text-center text-muted-foreground">
                    This conversation has been closed
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <MessageCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Select a conversation to start chatting</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
